import { useEffect, useState } from "react";
import { loadConnectAndInitialize } from "@stripe/connect-js";
import { DEFAULT_ACCENT_COLOR } from "@/lib/brand-colors";
import {
  ConnectComponentsProvider,
  ConnectAccountOnboarding,
  ConnectAccountManagement,
  ConnectNotificationBanner,
} from "@stripe/react-connect-js";
import type { StripeConnectInstance } from "@stripe/connect-js";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";

type ClientSecretResponse = {
  clientSecret: string;
  publishableKey: string | null;
};

function getErrorMessage(e: unknown, fallback: string): string {
  if (e instanceof Error) return e.message;
  if (typeof e === "string") return e;
  return fallback;
}

async function fetchAccountSession(): Promise<ClientSecretResponse> {
  const r = await fetch("/api/vendor-portal/payout-account/client-secret", { credentials: "include" });
  if (!r.ok) {
    const err = (await r.json().catch(() => ({}))) as { message?: string };
    throw new Error(err.message || `Request failed: ${r.status}`);
  }
  return (await r.json()) as ClientSecretResponse;
}

function useConnectInstance(enabled: boolean) {
  const [instance, setInstance] = useState<StripeConnectInstance | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      try {
        const initial = await fetchAccountSession();
        if (!initial.publishableKey) throw new Error("Stripe publishable key is not configured");
        // Reuse the secret from the bootstrap fetch on the first call so we
        // don't issue two `/account_sessions` requests just to initialize.
        // Subsequent refreshes (driven by Stripe) hit the endpoint normally.
        let pending: string | null = initial.clientSecret;
        const inst = await loadConnectAndInitialize({
          publishableKey: initial.publishableKey,
          fetchClientSecret: async () => {
            if (pending) {
              const s = pending;
              pending = null;
              return s;
            }
            const next = await fetchAccountSession();
            return next.clientSecret;
          },
          appearance: {
            overlays: "dialog",
            variables: {
              colorPrimary: DEFAULT_ACCENT_COLOR,
            },
          },
        });
        if (!cancelled) setInstance(inst);
      } catch (e: unknown) {
        if (!cancelled) setError(getErrorMessage(e, "Failed to load Stripe"));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { instance, error };
}

export function VendorPayoutNotificationBanner({ onActionRequired }: { onActionRequired?: (hasActions: boolean) => void }) {
  const { instance, error } = useConnectInstance(true);
  if (error || !instance) return null;
  return (
    <div data-testid="stripe-notification-banner">
      <ConnectComponentsProvider connectInstance={instance}>
        <ConnectNotificationBanner
          collectionOptions={{ fields: "eventually_due", futureRequirements: "include" }}
          onNotificationsChange={(e) => onActionRequired?.((e.total ?? 0) > 0)}
        />
      </ConnectComponentsProvider>
    </div>
  );
}

export function VendorPayoutOnboardingDialog({
  open,
  onOpenChange,
  onExit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onExit?: () => void;
}) {
  const { instance, error } = useConnectInstance(open);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-stripe-onboarding">
        <DialogHeader>
          <DialogTitle>Set up direct payouts</DialogTitle>
          <DialogDescription>
            Connect your bank account through Stripe to receive direct payouts from PadPulse.
          </DialogDescription>
        </DialogHeader>
        {error ? (
          <div className="text-sm text-red-600 py-4" data-testid="text-stripe-onboarding-error">{error}</div>
        ) : !instance ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          </div>
        ) : (
          <ConnectComponentsProvider connectInstance={instance}>
            <ConnectAccountOnboarding
              onExit={() => {
                onExit?.();
                onOpenChange(false);
              }}
            />
          </ConnectComponentsProvider>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function VendorPayoutManagementDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { instance, error } = useConnectInstance(open);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-stripe-management">
        <DialogHeader>
          <DialogTitle>Manage payout account</DialogTitle>
          <DialogDescription>
            Update your bank details, business info, and Stripe account settings.
          </DialogDescription>
        </DialogHeader>
        {error ? (
          <div className="text-sm text-red-600 py-4" data-testid="text-stripe-management-error">{error}</div>
        ) : !instance ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          </div>
        ) : (
          <ConnectComponentsProvider connectInstance={instance}>
            <ConnectAccountManagement />
          </ConnectComponentsProvider>
        )}
      </DialogContent>
    </Dialog>
  );
}
